<script setup lang="ts">
import { Menu, X } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AccountModal from './components/AccountModal.vue'
import DebugPanel from './components/DebugPanel.vue'
import FeedbackModal from './components/FeedbackModal.vue'
import SidebarFootActions from './components/SidebarFootActions.vue'
import type { NavItem } from './config/nav'
import { useTestActions } from './composables/useTestAction'
import { useModules } from './moduleAdapter'
import { useAuthStore } from './stores/auth'
import { useSiteStore } from './stores/site'
import logoSrc from './assets/logo.trans.svg'

const isDev = import.meta.env.DEV

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const siteStore = useSiteStore()

const allModules = useModules()

const availableTypes = allModules.map((m) => ({ key: m.key, label: m.label }))
const storedType = localStorage.getItem('sidebar_active_type')
const activeType = ref(
  availableTypes.some((t) => t.key === storedType)
    ? (storedType as string)
    : (availableTypes[0]?.key || ''),
)

const collapsed = ref(localStorage.getItem('sidebar_second_collapsed') === 'true')
const mobileMenuOpen = ref(false)
const accountVisible = ref(false)
const accountSection = ref<string | undefined>(undefined)
const feedbackVisible = ref(false)

/** 打开账号弹窗；传入 section 可直接定位到某个分节（如「点数充值」） */
function openAccountModal(section?: string) {
  accountSection.value = section
  accountVisible.value = true
}

// 供业务页面（如首页钱包卡）跨组件唤起账号弹窗；用一个窗口事件而不是把弹窗状态下沉
function handleAccountEvent(e: Event) {
  openAccountModal((e as CustomEvent<{ section?: string }>).detail?.section)
}

onMounted(() => window.addEventListener('musex:account', handleAccountEvent))
onBeforeUnmount(() => window.removeEventListener('musex:account', handleAccountEvent))

useTestActions({
  'shell.account.open.prepare': () => {
    accountVisible.value = true
  },
  'shell.account.open.cleanup': () => {
    accountVisible.value = false
  },
})

const currentModule = computed(() => allModules.find((m) => m.key === activeType.value))
const homePath = computed(() => currentModule.value?.homePath || '/')

// 模块菜单：普通用户过滤掉 adminOnly 入口（监控 / 日志 / 系统 / 管理类）
const menuEntries = computed<NavItem[]>(() => {
  const menu = currentModule.value?.getMenu() ?? []
  return auth.isAdmin ? menu : menu.filter((e) => !e.adminOnly)
})
const railEntries = computed(() => menuEntries.value.filter((e) => !e.footer))
const settingsEntry = computed(() => menuEntries.value.find((e) => e.footer) ?? null)

const entryHasSecond = (entry: NavItem | null) => !!entry?.items?.length

// 依据当前路由推断激活的一级入口与二级项（二级优先，其次一级直接项）
const routeMatch = computed(() => {
  const path = route.path
  for (const entry of menuEntries.value) {
    for (const item of entry.items ?? []) {
      if (item.path && (path === item.path || path.startsWith(item.path + '/'))) {
        return { entryKey: entry.key, itemKey: item.key }
      }
    }
  }
  for (const entry of menuEntries.value) {
    if (entry.path && (path === entry.path || path.startsWith(entry.path + '/'))) {
      return { entryKey: entry.key, itemKey: entry.key }
    }
  }
  return { entryKey: menuEntries.value[0]?.key ?? '', itemKey: '' }
})

const activeEntryKey = computed(() => routeMatch.value.entryKey)
const activeItemKey = computed(() => routeMatch.value.itemKey)
const activeEntry = computed(
  () => menuEntries.value.find((e) => e.key === activeEntryKey.value) ?? null,
)
const activeSecondItems = computed(() => activeEntry.value?.items ?? [])

const showSecond = computed(() => entryHasSecond(activeEntry.value) && !collapsed.value)
const isSettingActive = computed(() => activeEntry.value?.footer === true)

function toggleCollapsed() {
  collapsed.value = !collapsed.value
  localStorage.setItem('sidebar_second_collapsed', String(collapsed.value))
}

// 移动端：点击带二级内容的一级入口后，抑制路由变化触发的抽屉关闭
let keepMobileOpen = false

function handleEntryClick(entry: NavItem) {
  if (activeEntryKey.value === entry.key && entryHasSecond(entry)) {
    toggleCollapsed()
    return
  }
  const path = entry.items?.[0]?.path || entry.path
  if (entryHasSecond(entry)) {
    if (collapsed.value) toggleCollapsed()
    if (mobileMenuOpen.value) keepMobileOpen = true
  }
  if (path) router.push(path)
  if (!entryHasSecond(entry)) mobileMenuOpen.value = false
}

function handleMenuClick(item: NavItem) {
  if (item.path) router.push(item.path)
  mobileMenuOpen.value = false
}

function handleSettingsClick() {
  const entry = settingsEntry.value
  if (!entry) return
  if (collapsed.value) toggleCollapsed()
  const path = entry.items?.[0]?.path
  if (path) router.push(path)
  mobileMenuOpen.value = false
}

function handleTypeSwitch(key: string) {
  activeType.value = key
  const m = allModules.find((mod) => mod.key === key)
  if (m) router.push(m.homePath)
}

watch(
  () => route.path,
  (path) => {
    for (const m of allModules) {
      if (m.pathPrefixes.some((p) => path === p || path.startsWith(p + '/'))) {
        if (activeType.value !== m.key) activeType.value = m.key
        break
      }
    }
  },
  { immediate: true },
)

watch(activeType, (val) => localStorage.setItem('sidebar_active_type', val))

watch(
  () => route.name,
  () => {
    if (keepMobileOpen) {
      keepMobileOpen = false
      return
    }
    mobileMenuOpen.value = false
  },
)
</script>

<template>
  <div class="relative min-h-screen bg-slate-50 dark:bg-[#09090b] transition-colors duration-300">
    <!-- Mobile Top Bar -->
    <div class="md:hidden flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-[#09090b] border-b border-gray-200/70 dark:border-white/10 sticky top-0 z-30">
      <div
        class="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
        @click="mobileMenuOpen = !mobileMenuOpen"
      >
        <X v-if="mobileMenuOpen" class="w-5 h-5" />
        <Menu v-else class="w-5 h-5" />
      </div>
      <div class="flex items-center gap-2 cursor-pointer" @click="router.push(homePath)">
        <img :src="logoSrc" class="w-6 h-6 object-contain" alt="logo" />
        <div class="text-base font-bold text-gray-900 dark:text-gray-100">{{ siteStore.displayName }}</div>
      </div>
    </div>

    <!-- Mobile Backdrop -->
    <Transition name="fade">
      <div
        v-if="mobileMenuOpen"
        class="fixed inset-0 bg-black/40 z-40 md:hidden"
        @click="mobileMenuOpen = false"
      />
    </Transition>

    <!-- Page Body：左侧菜单贴边融合，右侧内容为左上/右上圆角面板 -->
    <div
      class="relative transition-all duration-300 md:pt-3"
      :class="showSecond ? 'md:pl-[17rem]' : 'md:pl-[4rem]'"
    >
      <!-- Sidebar：贴左，无圆角；左列一级菜单 + 右列二级菜单 -->
      <aside
        class="fixed left-0 z-50 flex shrink-0 overflow-hidden transition-all duration-300 bg-slate-50 dark:bg-[#09090b]"
        :class="[
          'top-0 h-full',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          showSecond ? 'w-[17rem]' : 'w-[4.5rem]',
        ]"
      >
        <!-- 一级菜单列（贴左，图标 + 短标签） -->
        <div class="shrink-0 w-[4.5rem] flex flex-col items-center px-1 pb-3">
          <!-- 收起时 Logo 停在竖条顶部；展开时移至二级列顶部 -->
          <div v-if="!showSecond" class="h-16 flex items-center justify-center w-full shrink-0 cursor-pointer" @click="router.push(homePath)">
            <img :src="logoSrc" class="w-8 h-8 object-contain" alt="logo" />
          </div>
          <div v-else class="h-16 w-full shrink-0"></div>

          <nav class="flex-1 min-h-0 w-full overflow-y-auto flex flex-col items-center gap-1">
            <a-tooltip v-for="entry in railEntries" :key="entry.key" :title="entry.label" placement="right">
              <div
                class="w-full flex flex-col items-center gap-1.5 py-1.5 rounded-xl cursor-pointer transition-all duration-200"
                :class="activeEntryKey === entry.key
                  ? 'text-primary'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
                :data-testid="'nav-group-' + entry.key"
                @click="handleEntryClick(entry)"
              >
                <div
                  class="w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-200"
                  :class="activeEntryKey === entry.key
                    ? 'bg-primary/10'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800/60'"
                >
                  <component :is="entry.icon" class="w-6 h-6" aria-hidden="true" />
                </div>
                <span
                  class="text-[11px] leading-4 truncate max-w-[56px]"
                  :class="activeEntryKey === entry.key ? 'font-medium' : 'font-normal'"
                >{{ entry.shortLabel || entry.label }}</span>
              </div>
            </a-tooltip>
          </nav>

          <div class="shrink-0 w-full pt-2 flex justify-center">
            <SidebarFootActions
              :setting-active="isSettingActive"
              :show-setting="!!settingsEntry"
              @user="openAccountModal()"
              @feedback="feedbackVisible = true"
              @setting="handleSettingsClick"
            />
          </div>
        </div>

        <!-- 二级菜单列 -->
        <div v-show="showSecond" class="flex-1 flex flex-col min-w-0">
          <div class="h-16 flex items-center gap-2 px-3 cursor-pointer shrink-0" @click="router.push(homePath)">
            <img :src="logoSrc" class="w-8 h-8 object-contain shrink-0" alt="logo" />
            <div class="text-lg font-bold text-primary tracking-tight truncate">{{ siteStore.displayName }}</div>
          </div>
          <nav class="flex-1 min-h-0 overflow-y-auto px-2 pb-3 space-y-1">
            <div
              v-for="item in activeSecondItems"
              :key="item.key"
              class="w-full flex items-center gap-2 px-2.5 py-2 rounded-xl transition-all duration-200 cursor-pointer"
              :class="activeItemKey === item.key
                ? 'bg-primary/10 text-primary'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-700 dark:hover:text-gray-300'"
              :data-testid="'nav-item-' + item.key"
              @click="handleMenuClick(item)"
            >
              <component
                :is="item.icon"
                class="w-[18px] h-[18px] shrink-0"
                :class="activeItemKey === item.key ? 'text-primary' : 'text-gray-400 dark:text-gray-500'"
                aria-hidden="true"
              />
              <span
                class="text-[13.5px] leading-5 truncate"
                :class="activeItemKey === item.key ? 'font-semibold' : 'font-medium'"
              >{{ item.label }}</span>
            </div>
          </nav>
        </div>
      </aside>

      <!-- Main Content：左上/右上圆角面板，左侧贴菜单，底部直达视口底部 -->
      <main
        class="ml-2 relative min-w-0 overflow-x-clip md:mr-3 md:rounded-t-3xl md:shadow-sm md:border-l md:border-t md:border-r border-gray-200/70 dark:border-white/10 flex flex-col bg-white dark:bg-zinc-900 min-h-screen md:min-h-[calc(100vh-0.75rem)]"
      >
        <div class="flex-1 min-h-0 flex flex-col px-3 pt-3 pb-4 md:px-4 md:pt-4 md:pb-6">
          <div class="w-full">
            <RouterView />
          </div>
        </div>
      </main>
    </div>

    <!-- DebugPanel（仅开发模式） -->
    <DebugPanel
      v-if="isDev && availableTypes.length > 1"
      :modules="availableTypes"
      :active-type="activeType"
      @type-switch="handleTypeSwitch"
    />

    <AccountModal
      v-model:open="accountVisible"
      :sections="currentModule?.accountSections"
      :initial-tab="accountSection"
    />
    <FeedbackModal v-model:open="feedbackVisible" />
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
